import queryString from 'query-string';
import jwt from 'jsonwebtoken';
import LogRocket from 'logrocket';
import idx from 'idx';

export const ID_TOKEN = 'idToken';
export const ACCESS_TOKEN = 'accessToken';
export const EXPIRES_AT = 'expiresAt';
export const USER = 'user';

// Legacy functions - DEPRECATED: Use Auth0 React SDK instead
// eslint-disable-next-line
export const setSession = (authResult, history) => {
  LogRocket.identify(
    idx(authResult, _ => _.user.id),
    {
      name: `${idx(authResult, _ => _.user.firstName)} ${idx(
        authResult,
        _ => _.user.lastName,
      )}`,
      email: idx(authResult, _ => _.user.email),
    },
  );
  localStorage.setItem(ID_TOKEN, authResult.idToken);
  localStorage.setItem(EXPIRES_AT, authResult.expiresAt);
  if (authResult.user) {
    localStorage.setItem(USER, JSON.stringify(authResult.user));
  }
  if (history) {
    return history.replace('/');
  }
};

const getInitials = (firstName, lastName, email) => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`;
  }
  if (firstName) {
    return `${firstName.charAt(0)}`;
  }
  if (email) {
    return `${email.charAt(0)}`;
  }
  return '🙂';
};

export const handleAuthentication = history => {
  const { idToken, expiresAt } = queryString.parse(history.location.hash);

  if (history && idToken && expiresAt) {
    const { sub, firstname, lastname, email: emailA, ...rest } = jwt.decode(
      idToken,
    );
    const lastName = lastname || null;
    const firstName = firstname || null;
    const email = emailA && emailA.length && emailA[0];
    const initials = getInitials(firstName, lastName, email);
    const userRole =
      rest['https://hasura.io/jwt/claims']['x-hasura-default-role'];
    const user = {
      id: sub,
      firstName,
      lastName,
      email,
      initials,
      userRole,
    };

    return setSession(
      {
        idToken,
        expiresAt,
        user,
      },
      history,
    );
  }
  // eslint-disable-next-line
  return history.replace('/login');
};

export const getTokens = () => {
  const idToken = localStorage.getItem(ID_TOKEN);
  const expiresAt = localStorage.getItem(EXPIRES_AT);
  const user = JSON.parse(localStorage.getItem(USER));

  return {
    idToken,
    expiresAt,
    user,
  };
};

export const logOut = () => {
  // Clear access token and ID token from local storage
  localStorage.removeItem(ID_TOKEN);
  localStorage.removeItem(EXPIRES_AT);
  localStorage.removeItem(USER);
  return window.location.replace('/login');
};

// Legacy function - now returns false since we're using Auth0 React SDK
export const isAuthenticated = () => {
  // This function is kept for backward compatibility but should not be used
  // with Auth0 React SDK. Use useAuth0 hook instead.
  return false;
};

export const getUser = () => {
  const { user, idToken } = getTokens();
  const authenticated = isAuthenticated();
  return {
    user,
    isAuthenticated: authenticated,
    idToken,
  };
};

// New Auth0 utility functions
// Get user role from the stored backend JWT token
export const getUserRoleFromToken = () => {
  const { idToken } = getTokens();

  if (!idToken) {
    return 'user';
  }

  try {
    const decodedToken = jwt.decode(idToken);
    const hasuraClaims = decodedToken?.['https://hasura.io/jwt/claims'];
    return hasuraClaims?.['x-hasura-default-role'] || 'user';
  } catch (error) {
    console.error('Error decoding JWT token for role:', error);
    return 'user';
  }
};

export const getAuth0User = user => {
  if (!user) {
    return null;
  }

  const getInitialsFromAuth0 = (name, email) => {
    if (name) {
      const nameParts = name.split(' ');
      if (nameParts.length >= 2) {
        return `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`;
      }
      return name.charAt(0);
    }
    if (email) {
      return email.charAt(0);
    }
    return '🙂';
  };

  return {
    id: user.sub,
    name: user.name,
    email: user.email,
    picture: user.picture,
    initials: getInitialsFromAuth0(user.name, user.email),
    // Extract roles from the backend JWT token (not Auth0 user)
    userRole: getUserRoleFromToken(),
  };
};

export const setupLogRocketForAuth0 = user => {
  if (user && LogRocket) {
    const auth0User = getAuth0User(user);
    LogRocket.identify(auth0User.id, {
      name: auth0User.name,
      email: auth0User.email,
    });
  }
};

// Helper function to get Auth0 access token for GraphQL requests
export const getAuth0AccessToken = async getAccessTokenSilently => {
  try {
    const token = await getAccessTokenSilently();
    return token;
  } catch (error) {
    return null;
  }
};

// Helper function to check if user has required role
export const hasRole = (user, requiredRole) => {
  if (!user) {
    return false;
  }
  return user.userRole === requiredRole;
};

// Helper function to check if user is admin
export const isAdmin = user => {
  return hasRole(user, 'global') || hasRole(user, 'admin');
};
